import { createAgentStream, type AgentToolCall, type HistoryMessage } from "@/apis/agentStream";

export type { HistoryMessage } from "@/apis/agentStream";

/**
 * Chat session over the AG-UI stream (#672): one instance per conversation,
 * rebuilt from persisted turns when an old conversation is resumed. Runs the
 * client-tool continuation loop and shapes stream events into the plain
 * callbacks below, so UI code and tests never see the transport.
 */

export interface AgentRunCallbacks {
  /** Accumulated assistant text so far (not a delta). */
  onText(fullText: string): void;
  onToolStart(name: string): void;
  onToolEnd(name: string): void;
  onError(message: string): void;
}

/**
 * A tool executed by the browser (#674): declared to the model per run; when the
 * model calls it the server ends the run, this client executes, appends the
 * result as a tool message, and continues the run.
 */
export interface ClientTool {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  /** Runs the tool; a returned promise is awaited before the result is serialized. */
  execute(args: Record<string, unknown>): unknown;
}

export interface AgentChatSession {
  readonly threadId: string;
  run(
    text: string,
    forwardedProps: Record<string, unknown>,
    callbacks: AgentRunCallbacks,
    clientTools?: ClientTool[],
  ): Promise<void>;
  abort(): void;
}

/** Client-tool continuations allowed per user message (runaway protection). */
const MAX_CLIENT_TOOL_ROUNDS = 3;

/** Per-call execution budget (#675): a hung tool becomes an error result, not a stalled turn. */
const CLIENT_TOOL_TIMEOUT_MS = 10_000;

async function executeClientTool(
  tool: ClientTool | undefined,
  call: AgentToolCall,
): Promise<string> {
  if (!tool) {
    return JSON.stringify({ error: `Unknown client tool "${call.name}".` });
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () =>
          reject(
            new Error(
              `Tool "${call.name}" timed out after ${CLIENT_TOOL_TIMEOUT_MS / 1000} seconds.`,
            ),
          ),
        CLIENT_TOOL_TIMEOUT_MS,
      );
    });
    const result = await Promise.race([Promise.resolve(tool.execute(call.args)), timeout]);
    return JSON.stringify(result ?? null);
  } catch (error: unknown) {
    return JSON.stringify({ error: error instanceof Error ? error.message : String(error) });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Names the tools a silent run called so the message points at what went wrong
 * (a call nothing executed) instead of a bare "empty answer".
 */
function emptyAnswerMessage(calledTools: readonly string[]): string {
  const names = [...new Set(calledTools)];
  const detail =
    names.length > 0 ? ` It called ${names.join(", ")} but nothing produced a reply.` : "";
  return `The assistant returned an empty answer.${detail} Try again or rephrase.`;
}

type ClientToolResult = { call: AgentToolCall; content: string };

/**
 * A finished round with neither text nor a client tool must not end in silence
 * (the model called a tool that exists nowhere). An error the stream already
 * reported is left as-is.
 */
function reportIfEmptyAnswer(
  committedText: string,
  runErrored: boolean,
  calledTools: readonly string[],
  onError: AgentRunCallbacks["onError"],
): void {
  if (committedText !== "" || runErrored) {
    return;
  }
  onError(emptyAnswerMessage(calledTools));
}

/**
 * Client tools act on shared UI state (route, filters, selection) and read it
 * back, so they run one at a time. Returns null when this run was superseded:
 * a tool that already finished keeps its side effect, and the result is dropped
 * instead of being appended onto the replacement turn.
 */
async function executePendingTools(
  pending: readonly AgentToolCall[],
  clientTools: readonly ClientTool[],
  superseded: () => boolean,
): Promise<ClientToolResult[] | null> {
  const results: ClientToolResult[] = [];
  for (const call of pending) {
    if (superseded()) {
      return null;
    }
    const content = await executeClientTool(
      clientTools.find((tool) => tool.name === call.name),
      call,
    );
    if (superseded()) {
      return null;
    }
    results.push({ call, content });
  }
  return results;
}

export function createAgentChatSession(
  threadId: string,
  history: HistoryMessage[],
): AgentChatSession {
  const stream = createAgentStream(threadId, history);
  // Bumps on abort and on each new run. A superseded run must not append tool
  // results, start another round, or roll this session's history back.
  let generation = 0;

  async function runRounds(
    forwardedProps: Record<string, unknown>,
    callbacks: AgentRunCallbacks,
    clientTools: ClientTool[],
    token: number,
  ): Promise<void> {
    const toolDeclarations = clientTools.map(({ name, description, parameters }) => ({
      name,
      description,
      parameters,
    }));
    const clientToolNames = new Set(clientTools.map((t) => t.name));
    const superseded = () => token !== generation;

    // One logical turn can stream several assistant text segments (text around
    // tool rounds, or across client-tool continuations), and the per-message
    // buffer resets between them. Accumulate segments so a later segment (or an
    // empty end-of-message buffer) can only ever extend the visible answer,
    // never replace or wipe it.
    let committedText = "";
    let runErrored = false;
    const calledTools: string[] = [];
    const joinSegments = (buffer: string) =>
      committedText ? `${committedText}\n\n${buffer}` : buffer;
    const emitText = (buffer: string | undefined) => {
      const current = buffer ? joinSegments(buffer) : committedText;
      if (current.length > 0) {
        callbacks.onText(current);
      }
    };

    for (let round = 0; round <= MAX_CLIENT_TOOL_ROUNDS; round++) {
      if (superseded()) {
        return;
      }
      const pending: AgentToolCall[] = [];

      await stream.run(
        forwardedProps,
        toolDeclarations,
        {
          onTextContent: (buffer) => {
            emitText(buffer);
          },
          onTextEnd: (buffer) => {
            if (buffer) {
              committedText = joinSegments(buffer);
            }
            emitText(undefined);
          },
          onToolCallStart: (name) => {
            calledTools.push(name);
            callbacks.onToolStart(name);
          },
          onToolCallEnd: (call) => {
            callbacks.onToolEnd(call.name);
            // The server can stream one call id twice (the raw call and its approval
            // request); a call executes once, whichever copy arrives first.
            if (
              clientToolNames.has(call.name) &&
              !pending.some((p) => p.toolCallId === call.toolCallId)
            ) {
              pending.push(call);
            }
          },
          onRunError: (message) => {
            runErrored = true;
            callbacks.onError(message ?? "The agent run failed.");
          },
        },
        superseded,
      );

      if (superseded()) {
        return;
      }

      if (pending.length === 0) {
        reportIfEmptyAnswer(committedText, runErrored, calledTools, (message) => {
          callbacks.onError(message);
        });
        return;
      }

      const results = await executePendingTools(pending, clientTools, superseded);
      if (results === null) {
        return;
      }
      if (superseded()) {
        return;
      }
      stream.addToolResults(results);
    }

    if (superseded()) {
      return;
    }
    callbacks.onError(`The agent exceeded ${MAX_CLIENT_TOOL_ROUNDS} screen-tool continuations.`);
  }

  return {
    threadId,

    async run(text, forwardedProps, callbacks, clientTools = []) {
      const token = ++generation;
      // A previous run that was interrupted mid tool call can leave an assistant
      // tool call with no result in the shared history. Dropping it before this
      // turn's user message keeps the model API from rejecting the request.
      stream.pruneDanglingToolCalls();
      // On failure the history must roll back to this point: a message left behind
      // by a failed run would be re-sent (and duplicated) by every retry.
      // A superseded run must not restore: abort() can reject this stream after
      // the replacement run has already appended its own user message.
      const restore = stream.checkpoint();
      stream.addUserMessage(text);

      try {
        await runRounds(forwardedProps, callbacks, clientTools, token);
      } catch (error: unknown) {
        if (token === generation) {
          restore();
        }
        throw error;
      }
    },

    abort() {
      generation += 1;
      stream.abort();
    },
  };
}
